// src/views/Client/ClientEmail.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { orderBy, query, onSnapshot, getDoc, addDoc, doc, getDocs, collection, serverTimestamp,setDoc  } from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { buildFunctionUrl } from '../../config/functions';
import './ClientEmail.css';
import { sendNotification } from '../../utils/sendNotification';

const ClientEmail = () => {
  const { clientId } = useParams();
  const [email, setEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [status, setStatus] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [messages, setMessages] = useState([])
  
  useEffect(() => {
    const fetchEmail = async () => {
      try {
        const ref = doc(db, 'clients', clientId);
        const snap = await getDoc(ref);
  
        if (snap.exists()) {
          const data = snap.data();
          setEmail(data.email);
          setClientPhone(data.phone || '');
          return;
        }
  
        const email = decodeURIComponent(clientId).toLowerCase();
        const snapshot = await getDocs(collection(db, 'clients'));
        const match = snapshot.docs.find(doc => doc.data().email?.toLowerCase() === email);
  
        if (!match) throw new Error('Cliente no encontrado');

        const data = match.data();
        setEmail(data.email);
        setClientPhone(data.phone || '');
      } catch (err) {
        console.error('❌ Error al cargar email del cliente:', err);
        setStatus('error');
      }
    };
  
    if (clientId) fetchEmail();
  }, [clientId]);
  useEffect(() => {
    if (!clientId) return;
  
    try {
      const q = query(
        collection(db, 'conversations', clientId, 'messages'),
        orderBy('timestamp')
      );
  
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('[Firestore] Mensajes:', msgs);
        setMessages(msgs);
      });
  
      return () => unsubscribe();
    } catch (err) {
      console.error('❌ Error al escuchar mensajes:', err);
    }
  }, [clientId]);
  
  
  const formatPhoneForSms = (value = '') => {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('1') && digits.length === 11) {
      return `+${digits}`;
    }
    if (digits.length === 10) {
      return `+1${digits}`;
    }
    return value.startsWith('+') ? value : `+${digits}`;
  };

  const notifyClientViaSms = async (subjectLine, bodyText) => {
    const formattedPhone = formatPhoneForSms(clientPhone);
    if (!formattedPhone) return;
    const preview = `${subjectLine || 'Notificación PRWD'} · ${bodyText}`.trim().slice(0, 150);
    try {
      await sendNotification('sms', {
        to: formattedPhone,
        message: preview,
      });
    } catch (smsError) {
      console.warn('⚠️ No se pudo enviar SMS al cliente:', smsError.message || smsError);
    }
  };

  const sendEmail = async (e) => {
    e.preventDefault();
    setStatus('sending');
  
    try {
      // Enviar correo
      const res = await fetch(buildFunctionUrl('/sendClientEmail'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: email,
          subject,
          text: body,
          html: `<p>${body}</p>`,
          attachments: attachments || [],
          
        }),
      });
  
      if (!res.ok) throw new Error('Fallo el envío del email');
  
      // 🔹 Asegurar que la conversación exista
      const convoRef = doc(db, 'conversations', clientId);
      await setDoc(convoRef, { updatedAt: serverTimestamp() }, { merge: true });
  
      // 🔹 Guardar el mensaje en la subcolección
      const msgRef = collection(db, 'conversations', clientId, 'messages');
      await addDoc(msgRef, {
        from: 'admin',
        subject,
        body,
        timestamp: serverTimestamp(),
        attachments: attachments || [],
        read: false,
        
      });
  
      console.log('[✅] Mensaje guardado correctamente');
  
      // Limpiar estado
      setStatus('sent');
      await notifyClientViaSms(subject, body);

      setSubject('');
      setBody('');
      setAttachments([]);
    } catch (err) {
      console.error('❌ Error al enviar o guardar mensaje:', err);
      setStatus('error');
    }
  };
  
  const handleFiles = (e) => {
    const files = Array.from(e.target.files);
    const promises = files.map(file => {
      return new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            content: reader.result.split(',')[1],
            filename: file.name,
            type: file.type,
          });
        };
        reader.readAsDataURL(file);
      });
    });
  
    Promise.all(promises).then(setAttachments);
  };

  return (
    <div className="email-container">
      <h3>Mensajería</h3>
  
      <div className="chat-box">
        <div className="chat-history">
          {messages.map(msg => (
            <div key={msg.id} className={`message ${msg.from === 'admin' ? 'sent' : 'received'}`}>
              <div>
                <strong>{msg.from}</strong> • {msg.timestamp?.toDate().toLocaleString()}
              </div>
              <div>{msg.body}</div>
              {msg.attachments?.map((a, i) => (
                <div key={i}>
                  <a href={`data:${a.type};base64,${a.content}`} download={a.filename}>
                    {a.filename}
                  </a>
                </div>
              ))}
            </div>
          ))}
        </div>
  
        <form className="chat-input" onSubmit={sendEmail}>
          {status === 'error' && (
            <p style={{ color: 'red' }}>
              Error: no se pudo obtener el email o enviar el mensaje.
            </p>
          )}
  
          <input
            value={subject}
            onChange={e => setSubject(e.target.value)}
            placeholder="Asunto"
            required
            disabled={!email || status === 'sending'}
          />
  
          <textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Escribe tu mensaje..."
            required
            disabled={!email || status === 'sending'}
          />
  
          <input type="file" multiple onChange={handleFiles} />
  
          <button
            type="submit"
            disabled={!email || !subject || !body || status === 'sending'}
          >
            {status === 'sending' ? 'Enviando…' : 'Enviar'}
          </button>
  
          {status === 'sent' && (
            <p style={{ color: 'green' }}>Mensaje enviado ✅</p>
          )}
        </form>
      </div>
    </div>
  );
  
  
};

export default ClientEmail;
